import { FormEvent, useState, useTransition } from "react";
import { Activity, Target, Timer, UsersRound } from "lucide-react";
import { getRoomState } from "@/lib/api";
import type { RoomState } from "@/types/api";
import { formatNumber } from "@/lib/utils";

export function RoomInspector() {
  const [roomCode, setRoomCode] = useState("");
  const [state, setState] = useState<RoomState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!roomCode.trim()) return;
    startTransition(async () => {
      const data = await getRoomState(roomCode.trim());
      if (!data) {
        setError("Unable to locate room state. Verify the code and try again.");
        setState(null);
        return;
      }
      setError(null);
      setState(data);
    });
  };

  return (
    <section className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-900">
            <Activity className="h-5 w-5 text-neutral-500" aria-hidden />
            Live room inspector
          </h3>
          <p className="text-sm text-neutral-500">
            Fetch in-memory state snapshots for an active room code.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3 sm:flex-row">
        <input
          type="text"
          value={roomCode}
          onChange={(event) => setRoomCode(event.target.value.toUpperCase())}
          placeholder="ROOM123"
          className="flex-1 rounded-md border border-neutral-200 px-3 py-2 text-sm uppercase focus:border-neutral-400 focus:outline-none"
          maxLength={12}
        />
        <button
          type="submit"
          disabled={isPending}
          className="inline-flex items-center gap-2 rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800 disabled:opacity-70"
        >
          {isPending ? (
            "Fetching..."
          ) : (
            <>
              Inspect
              <Target className="h-4 w-4" aria-hidden />
            </>
          )}
        </button>
      </form>

      {error ? (
        <p className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">
          {error}
        </p>
      ) : null}

      {state ? (
        <div className="mt-6 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg border border-neutral-100 bg-neutral-50 p-4">
              <div className="flex items-center justify-between text-xs font-medium uppercase text-neutral-500">
                <span>Room code</span>
                <Target className="h-4 w-4 text-neutral-400" aria-hidden />
              </div>
              <p className="mt-1 text-lg font-semibold text-neutral-900">
                {state.roomCode}
              </p>
            </div>
            <div className="rounded-lg border border-neutral-100 bg-neutral-50 p-4">
              <div className="flex items-center justify-between text-xs font-medium uppercase text-neutral-500">
                <span>Quiz</span>
                <Activity className="h-4 w-4 text-neutral-400" aria-hidden />
              </div>
              <p className="mt-1 text-lg font-semibold text-neutral-900">
                {state.quizName || "—"}
              </p>
            </div>
            <div className="rounded-lg border border-neutral-100 bg-neutral-50 p-4">
              <div className="flex items-center justify-between text-xs font-medium uppercase text-neutral-500">
                <span>Status</span>
                <UsersRound className="h-4 w-4 text-neutral-400" aria-hidden />
              </div>
              <p className="mt-1 text-lg font-semibold text-neutral-900">
                {state.status}
              </p>
            </div>
            <div className="rounded-lg border border-neutral-100 bg-neutral-50 p-4">
              <div className="flex items-center justify-between text-xs font-medium uppercase text-neutral-500">
                <span>Current question</span>
                <Timer className="h-4 w-4 text-neutral-400" aria-hidden />
              </div>
              <p className="mt-1 text-lg font-semibold text-neutral-900">
                {state.currentQuestion ?? "—"}
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-neutral-100 bg-neutral-50 p-4">
            <h4 className="text-sm font-semibold text-neutral-800">
              Active players
            </h4>
            {state.players.length === 0 ? (
              <p className="mt-2 text-sm text-neutral-500">
                No participants connected to this room.
              </p>
            ) : (
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                {state.players.map((player, index) => (
                  <div
                    key={player.id ?? index}
                    className="rounded-md border border-neutral-200 bg-white p-3 text-sm text-neutral-700"
                  >
                    <div className="flex items-center justify-between">
                      <p className="font-semibold">
                        {player.username || player.name || player.id || "Unknown"}
                      </p>
                      <p className="text-xs text-neutral-500">
                        {formatNumber(player.points ?? 0)} pts
                      </p>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs text-neutral-500">
                      <span>Correct: {formatNumber(player.correct ?? 0)}</span>
                      <span>Incorrect: {formatNumber(player.incorrect ?? 0)}</span>
                      <span>Time: {formatNumber(player.timeInSec ?? 0)}s</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}


